import React, { useEffect, useMemo, useState } from 'react'
import { ClientForm } from './client/form'
import {
  CodonationWatcher,
  StripeContextProvider,
  buildInitialValues,
  buildValidationSchema,
  trackDonateFormVisit,
  useSubmitHandler,
} from './shared'
import { Config, Display, DisplayLayout, DonateFormProps, FormValueKeys, GAErrorTrackerProps } from './types'
import { Elements, useElements, useStripe } from '@stripe/react-stripe-js'
import { ErrorBoundary, gaEvent } from 'shared'
import { Formik } from 'formik'
import { IframeForm } from './iframe/form'
import { PlatformForm } from './platform/form'
import { SkiForm } from './ski/form'
import { type Stripe, loadStripe } from '@stripe/stripe-js'

const ErrorMessage = () => {
  return (
    <div className="pt-28 pb-8 text-red-700 text-center">
      <p>{i18n.t('errors.messages.default')}</p>
    </div>
  )
}

const InjectedFormSetup = (props: DonateFormProps) => {
  const stripe = useStripe()
  const elements = useElements()
  const value = useMemo(() => ({ stripe, elements }), [stripe, elements])
  return (
    <StripeContextProvider value={value}>
      <FormSetup {...props} />
    </StripeContextProvider>
  )
}

const useStripePromise = (stripeKey: string) => {
  const [promise, setPromise] = useState<Promise<Stripe | null> | null>(null)
  useEffect(() => {
    if (!stripeKey) return
    setPromise(loadStripe(stripeKey))
  }, [stripeKey])
  return promise
}

export const DonateForm = (props: DonateFormProps): JSX.Element => {
  const stripePromise = useStripePromise(props.config.stripePublicKey)
  return (
    <ErrorBoundary fallbackComponent={FormSetup} errorStateComponent={ErrorMessage} {...props}>
      <Elements stripe={stripePromise}>
        <InjectedFormSetup {...props} />
      </Elements>
    </ErrorBoundary>
  )
}

export const getFormComponent = (layout: DisplayLayout) => {
  switch (layout) {
    case 'donate/platform':
      return PlatformForm
    case 'donate/iframe':
      return IframeForm
    case 'donate/ski':
      return SkiForm
    default:
      return ClientForm
  }
}

export function useDonationTrackingFormVisit(config: Config, display: Display) {
  useEffect(() => trackDonateFormVisit({ config, display }), [config, display])
}

export const FormSetup = (props: DonateFormProps): JSX.Element => {
  const { config, display } = props

  useDonationTrackingFormVisit(config, display)
  const FormComponent = useMemo(() => getFormComponent(display.layout), [display.layout])
  const initialValues = useMemo(() => buildInitialValues({ config }), [config])
  const submitHandler = useSubmitHandler()

  const validationSchema = useMemo(() => buildValidationSchema(), [])

  return (
    <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={submitHandler}>
      {(formikProps) => (
        <>
          <GAErrorTracker errors={formikProps.errors} touched={formikProps.touched} />
          <CodonationWatcher />
          <FormComponent {...formikProps} {...props} />
        </>
      )}
    </Formik>
  )
}

const GAErrorTracker = ({ errors, touched }: GAErrorTrackerProps) => {
  const keys = Object.keys(errors) as FormValueKeys
  keys.forEach((field) => {
    if (touched[field]) {
      gaEvent('error', `field: ${field}`, `error: ${errors[field] as string}`, null, true)
    }
  })
  return null
}
